import { useState } from "react";
import { AlertTriangle, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sanitizeHtml } from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CRIMSON = "var(--brand)";
const KEEP_AS_IS = "keep_as_is";
const STEPS = ["Before you start", "Choose profiles", "What moves", "Profile details", "Contacts, stakeholders & addresses", "Rostering preferences", "Roster check", "Documents", "Final check"];
const DOC_STEP = 7;
const FINAL_STEP = 8;
const ROSTER_LABELS = { staff: "Preferred & incompatible workers", considerations: "Rostering considerations", reason: "Reason for incompatible workers" };

const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

function ProfileCard({ title, tone, p }) {
  return (
    <div className={`rounded-md border p-3 text-xs ${tone}`}>
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{title}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{p.display_name || p.name}</div>
      <div className="text-slate-600 mt-1 space-y-0.5">
        <div>Profile ID: {p.name}</div>
        <div>NDIS number: {p.ndis_number || "—"}</div>
        <div>Date of birth: {p.date_of_birth || "—"}</div>
        <div>Status: {p.status}</div>
      </div>
    </div>
  );
}

export function MergeParticipantsWizard({ open, onOpenChange, participants, onDone }) {
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState({ checked: false, correct: false });
  const [src, setSrc] = useState("");
  const [tgt, setTgt] = useState("");
  const [prev, setPrev] = useState(null);
  const [choices, setChoices] = useState({});
  const [rowSel, setRowSel] = useState({});
  const [rowRep, setRowRep] = useState({});
  const [rost, setRost] = useState({});
  const [fieldSel, setFieldSel] = useState({});
  const [shiftSel, setShiftSel] = useState({});
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(0); setReady({ checked: false, correct: false }); setSrc(""); setTgt(""); setPrev(null);
    setChoices({}); setRowSel({}); setRowRep({}); setRost({}); setFieldSel({}); setShiftSel({}); setTyped(""); setAck(false); setResult(null);
  };
  const close = (o) => {
    if (busy) return;
    if (!o) { if (result) onDone?.(); reset(); }
    onOpenChange(o);
  };

  const loadPreview = async () => {
    setBusy(true);
    try {
      const pv = await api.mergeParticipantsPreview(src, tgt);
      const sel = {};
      (pv.profile?.lists || []).forEach((l) => { sel[l.key] = l.source_rows.filter((r) => !r.already_on_target).map((r) => r.idx); });
      const fsel = {};
      (pv.profile?.fields || []).forEach((f) => { if (f.target_blank) fsel[f.key] = "duplicate"; });
      setPrev(pv); setChoices({}); setRowSel(sel); setRowRep({}); setRost({}); setFieldSel(fsel); setShiftSel({}); setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not check these profiles."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      setResult(await api.mergeParticipants({
        source: src, target: tgt, doc_choices: choices, confirmed_irreversible: true,
        row_choices: rowSel, row_replace: rowRep, rostering_choices: rost, field_choices: fieldSel, shift_choices: shiftSel }));
    } catch (e) { toast.error(e.response?.data?.detail || "The merge failed. Nothing further was changed — please contact support before retrying."); }
    finally { setBusy(false); }
  };

  const groups = prev?.doc_groups || [];
  const lists = prev?.profile?.lists || [];
  const ros = prev?.profile?.rostering;
  const needs = ros?.needs_choice || [];
  const rostDone = needs.every((k) => rost[k]);
  const toggleRep = (key, idx) => setRowRep((cur) => {
    const has = (cur[key] || []).includes(idx);
    return { ...cur, [key]: has ? cur[key].filter((i) => i !== idx) : [...(cur[key] || []), idx] };
  });
  const toggleRow = (key, idx) => setRowSel((cur) => {
    const has = (cur[key] || []).includes(idx);
    return { ...cur, [key]: has ? cur[key].filter((i) => i !== idx) : [...(cur[key] || []), idx] };
  });
  const fields = prev?.profile?.fields || [];
  const fieldsDone = fields.every((f) => fieldSel[f.key]);
  const roster = prev?.roster;
  const pairs = roster?.pairs || [];
  const shiftsDone = pairs.every((p) => shiftSel[p.key]);
  const names = (a) => (a.length ? a.map((x) => x.name).join(", ") : "none");
  const allChosen = groups.every((g) => choices[g.key]);
  const srcName = prev?.source.display_name || src;
  const tgtName = prev?.target.display_name || tgt;
  const canSubmit = ack && typed.trim().toUpperCase() === "MERGE" && !busy;

  const describeChoice = (g) => {
    const c = choices[g.key];
    if (c === KEEP_AS_IS) return "Leave every version exactly as it is now";
    const chosen = g.docs.find((d) => d.id === c);
    return chosen ? `"${chosen.title}" will be the current (${g.current_state}) version` : "";
  };

  let body;
  if (result) {
    body = (
      <div className="space-y-3" data-testid="merge-done">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold"><Check className="h-5 w-5" />Merge complete</div>
        <p className="text-sm text-slate-600">Everything from <b>{srcName}</b> now sits on <b>{tgtName}</b>. {srcName} has been set to Inactive.</p>
        {result.profile_changes?.length > 0 && (
          <div className="text-xs rounded-md border bg-slate-50 p-3"><div className="font-medium mb-1">Profile changes made</div>
            <ul className="list-disc ml-4 space-y-0.5">{result.profile_changes.map((c) => <li key={c}>{c}</li>)}</ul></div>)}
        {result.roster_check && (
          <div className={`text-xs rounded-md border p-3 ${result.roster_check.remaining_on_old_profile ? "border-red-300 bg-red-50 text-red-800" : "bg-slate-50"}`} data-testid="merge-roster-result">
            <div className="font-medium mb-1">Roster check</div>
            {result.roster_check.remaining_on_old_profile === 0
              ? <div>✔ No shifts are left on {srcName}. {tgtName} now has {result.roster_check.active_shifts_on_kept_profile} active shift{result.roster_check.active_shifts_on_kept_profile === 1 ? "" : "s"}.</div>
              : <div>{result.roster_check.remaining_on_old_profile} shift(s) still point at {srcName} — please contact support.</div>}
            {result.roster_check.cancelled_duplicate_copies.length > 0 && <div>Cancelled duplicate copies: {result.roster_check.cancelled_duplicate_copies.join(", ")}</div>}
          </div>)}
        {result.document_changes?.length > 0 && (
          <div className="text-xs rounded-md border bg-slate-50 p-3"><div className="font-medium mb-1">Document changes made</div>
            <ul className="list-disc ml-4 space-y-0.5">{result.document_changes.map((c) => <li key={c}>{c}</li>)}</ul></div>)}
        {result.skipped?.length > 0 && <div className="text-xs rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">Needs a manual look: {result.skipped.join("; ")}</div>}
        <p className="text-xs text-slate-500">Open {tgtName}'s profile and check the shifts and documents look right.</p>
        {result.reference && <p className="text-[11px] text-slate-500" data-testid="merge-reference">Reference <b>{result.reference}</b> — quote this to support if anything looks wrong. A safety copy of the original records was saved automatically.</p>}
      </div>
    );
  } else if (step === 0) {
    body = (
      <div className="space-y-3 text-sm text-slate-700">
        <p>Use this when the <b>same person has two profiles</b>. It moves everything from the duplicate onto the profile you keep, so there is one complete record.</p>
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800 text-xs flex gap-2" data-testid="merge-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div><b>This cannot be undone.</b> Once you submit at the end, shifts, documents and notes cannot be moved back to the duplicate profile from the app. A safety copy is saved automatically first, so support can recover things if something goes wrong — but treat this as permanent. We will ask you to double-check before anything changes.</div>
        </div>
        <label className="flex gap-2 text-xs items-start"><input type="checkbox" className="mt-0.5" checked={ready.checked} onChange={(e) => setReady({ ...ready, checked: e.target.checked })} data-testid="merge-ready-1" />
          I have opened both profiles and I am sure they are the same person.</label>
        <label className="flex gap-2 text-xs items-start"><input type="checkbox" className="mt-0.5" checked={ready.correct} onChange={(e) => setReady({ ...ready, correct: e.target.checked })} data-testid="merge-ready-2" />
          I know which profile has the correct, up-to-date details — that is the one I will keep.</label>
      </div>
    );
  } else if (step === 1) {
    const pick = (label, hint, v, set, other, id) => (
      <div>
        <Label className="text-xs font-semibold">{label}</Label>
        <p className="text-[11px] text-slate-500">{hint}</p>
        <select className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value={v} data-testid={id} onChange={(e) => set(e.target.value)}>
          <option value="">Select a profile…</option>
          {participants.filter((p) => p.name !== other && !/\(merged into /.test(p.display_name || "")).map((p) => <option key={p.name} value={p.name}>{p.display_name || p.name} ({p.name}){p.status === "Inactive" ? " — Inactive" : ""}</option>)}
        </select>
      </div>
    );
    body = (
      <div className="space-y-4">
        {pick("1. Which profile is the DUPLICATE?", "This is the one that will be retired. Its shifts, documents and notes move across.", src, setSrc, tgt, "merge-source")}
        {pick("2. Which profile do you want to KEEP?", "This is the one with the correct, up-to-date details. It stays active.", tgt, setTgt, src, "merge-target")}
        {src && tgt && <p className="text-xs text-slate-500">Check the order carefully — the duplicate is retired, the one you keep stays.</p>}
      </div>
    );
  } else if (step === 2 && prev) {
    const entries = Object.entries(prev.counts);
    body = (
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-2 items-center">
          <ProfileCard title="Duplicate — will be retired" tone="border-red-200 bg-red-50/50" p={prev.source} />
          <ProfileCard title="Keeping — stays active" tone="border-emerald-300 bg-emerald-50/50" p={prev.target} />
        </div>
        <div className="rounded-md border p-3 text-xs" data-testid="merge-counts">
          <div className="font-medium text-slate-700 mb-1">These records will move from {srcName} to {tgtName}:</div>
          {entries.length === 0 ? <div className="text-slate-500">Nothing is linked to the duplicate profile.</div>
            : <ul className="space-y-0.5">{entries.map(([k, n]) => <li key={k}><b>{n}</b> × {prev.count_labels[k]}</li>)}</ul>}
        </div>
        {(fields.length > 0 || lists.length > 0 || needs.length > 0 || pairs.length > 0) && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="merge-decisions-needed">
            You'll be asked to decide: {[fields.length > 0 && `${fields.length} profile detail${fields.length === 1 ? "" : "s"} that differ`, lists.length > 0 && "contacts, stakeholders and addresses to copy", needs.length > 0 && "rostering preferences", pairs.length > 0 && `${pairs.length} clashing shift${pairs.length === 1 ? "" : "s"}`].filter(Boolean).join(", ")}.
          </div>)}
        {prev.sil_conflict && <div className="text-xs rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">Both profiles have a SIL weekly charge. Neither will be changed — you'll need to fix this by hand afterwards.</div>}
      </div>
    );
  } else if (step === 3 && prev) {
    const opt = (f, v, text) => (
      <label className="flex gap-2 items-start text-xs cursor-pointer">
        <input type="radio" className="mt-0.5" name={`field-${f.key}`} checked={fieldSel[f.key] === v} onChange={() => setFieldSel({ ...fieldSel, [f.key]: v })} data-testid={`merge-field-${f.key}-${v}`} />
        <span>{text}</span>
      </label>
    );
    const show = (v) => (/<[a-z][\s\S]*>/i.test(v) ? <div className="mt-0.5 rounded border bg-white p-2 text-xs max-h-24 overflow-y-auto" dangerouslySetInnerHTML={{ __html: sanitizeHtml(v) }} /> : <div className="mt-0.5 rounded border bg-white p-2 text-xs whitespace-pre-wrap">{v}</div>);
    const groupsF = [...new Set(fields.map((f) => f.group))];
    body = fields.length === 0 ? (
      <div className="text-sm text-slate-600" data-testid="merge-no-fields">Nothing on the duplicate's profile differs from {tgtName}'s, so every detail stays as it is.</div>
    ) : (
      <div className="space-y-4">
        <p className="text-xs text-slate-600">These details are filled in on <b>{srcName}</b> and differ from <b>{tgtName}</b>. For each one choose which should be on {tgtName} after the merge. Details that are the same, or only filled in on {tgtName}, are not shown.</p>
        {groupsF.map((g) => (
          <div key={g} className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{g}</div>
            {fields.filter((f) => f.group === g).map((f) => (
              <fieldset key={f.key} className="rounded-md border p-3 space-y-2" data-testid={`merge-field-${f.key}`}>
                <legend className="px-1 text-xs font-semibold">{f.label}{f.target_blank && <span className="font-normal text-slate-400"> — blank on {tgtName}</span>}</legend>
                <div className="grid sm:grid-cols-2 gap-2 text-xs">
                  <div><div className="text-[10px] uppercase tracking-wide font-semibold text-red-700">Duplicate ({srcName})</div>{show(f.source)}</div>
                  <div><div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Keeping ({tgtName})</div>{f.target ? show(f.target) : <div className="mt-0.5 text-slate-400 text-xs">Blank</div>}</div>
                </div>
                {opt(f, "keep", `Keep ${tgtName}'s${f.target ? "" : " (leave blank)"}`)}
                {opt(f, "duplicate", "Use the duplicate's")}
                {f.target && f.long && opt(f, "both", "Keep both (duplicate's added underneath)")}
              </fieldset>))}
          </div>))}
      </div>
    );
  } else if (step === 6 && prev) {
    const cancelOpt = (pr, v, text, disabled) => (
      <label className={`flex gap-2 items-start text-xs ${disabled ? "text-slate-400" : "cursor-pointer"}`}>
        <input type="radio" className="mt-0.5" disabled={disabled} name={`shift-${pr.key}`} checked={shiftSel[pr.key] === v} onChange={() => setShiftSel({ ...shiftSel, [pr.key]: v })} data-testid={`merge-shift-${pr.key}-${v}`} />
        <span>{text}</span>
      </label>
    );
    const sv = (x) => `${x.date} ${x.start}–${x.end}, ${x.worker} (${x.status || "Scheduled"}) · ${x.id}`;
    body = (
      <div className="space-y-4">
        <div className="rounded-md border p-3 text-xs" data-testid="merge-roster-summary">
          <b>{roster.total}</b> active shift{roster.total === 1 ? "" : "s"} on {srcName} will be moved onto {tgtName}. After the merge nothing will be left on {srcName}, and the system checks this and tells you the result.
          {roster.shifts.length > 0 && (
            <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5">{roster.shifts.map((x) => <li key={x.id} className={x.clash ? "text-amber-700 font-medium" : ""}>{sv(x)}{x.clash ? " — clashes, see below" : ""}</li>)}</ul>)}
        </div>
        {(roster.invoiced > 0 || roster.approved_not_invoiced > 0) && (
          <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="merge-invoiced-warning">
            <b>Heads up for accounts:</b> {roster.invoiced > 0 && <>{roster.invoiced} of these shifts {roster.invoiced === 1 ? "is" : "are"} already <b>invoiced</b>. </>}
            {roster.approved_not_invoiced > 0 && <>{roster.approved_not_invoiced} shift{roster.approved_not_invoiced === 1 ? " has" : "s have"} an <b>approved timesheet</b> but {roster.approved_not_invoiced === 1 ? "isn't" : "aren't"} invoiced yet. </>}
            They will move to {tgtName} along with their invoice records, so future invoices and history will sit under {tgtName}. Let your accounts team know before you continue.
          </div>)}
        {pairs.length === 0 ? <div className="text-sm text-slate-600" data-testid="merge-no-clashes">No shift on {srcName} clashes with one on {tgtName} for the same worker, so there are no double-ups to sort out.</div> : (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">These shifts overlap with a shift already on <b>{tgtName}</b> for the <b>same worker</b> — they are probably the same shift entered twice. For each pair choose what to do.</p>
            {pairs.map((pr) => (
              <fieldset key={pr.key} className="rounded-md border p-3 space-y-1.5" data-testid={`merge-pair-${pr.key}`}>
                <div className="text-xs"><span className="text-red-700 font-semibold">From duplicate:</span> {sv(pr.duplicate_shift)}</div>
                <div className="text-xs"><span className="text-emerald-700 font-semibold">Already on {tgtName}:</span> {sv(pr.kept_shift)}</div>
                {cancelOpt(pr, "keep_both", "Keep both shifts (the worker will be double-booked)")}
                {cancelOpt(pr, "cancel_duplicate_copy", "Cancel the duplicate's copy — no charge, recorded in the shift's history", !pr.can_cancel_duplicate_copy)}
                {!pr.can_cancel_duplicate_copy && <div className="text-[10px] text-slate-500">This shift has been worked, approved or invoiced, so it can't be cancelled here.</div>}
              </fieldset>))}
          </div>)}
      </div>
    );
  } else if (step === 4 && prev) {
    const rowLine = (r) => <span><b>{r.label}</b>{Object.keys(r.details).length ? " — " + Object.entries(r.details).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(", ") : ""}</span>;
    body = lists.length === 0 ? (
      <div className="text-sm text-slate-600" data-testid="merge-no-lists">The duplicate has no contacts or stakeholders recorded, so there is nothing to copy across.</div>
    ) : (
      <div className="space-y-4">
        <p className="text-xs text-slate-600">Tick the people on <b>{srcName}</b> you want copied onto <b>{tgtName}</b>. Anything you leave unticked is <b>not</b> copied and is lost when the duplicate is retired. People already on {tgtName} are never duplicated.</p>
        {lists.map((l) => (
          <div key={l.key} className="rounded-md border p-3" data-testid={`merge-list-${l.key}`}>
            <div className="text-xs font-semibold text-slate-800 mb-2">{l.label}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wide font-semibold text-red-700 mb-1">On the duplicate ({srcName})</div>
                <div className="space-y-1.5">{l.source_rows.map((r) => (
                  <label key={r.idx} className={`flex gap-2 items-start ${r.already_on_target ? "text-slate-400" : "cursor-pointer"}`}>
                    <input type="checkbox" className="mt-0.5" disabled={r.already_on_target} checked={!r.already_on_target && (rowSel[l.key] || []).includes(r.idx)} onChange={() => toggleRow(l.key, r.idx)} data-testid={`merge-row-${l.key}-${r.idx}`} />
                    <span>{rowLine(r)}{r.already_on_target && !r.differs && <em className="block text-[10px]">Already on {tgtName} — nothing to copy</em>}</span>
                  </label>))}
                  {l.source_rows.filter((r) => r.already_on_target && r.differs).map((r) => (
                    <div key={`d${r.idx}`} className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid={`merge-differs-${l.key}-${r.idx}`}>
                      <div><b>{r.label}</b> is on both profiles, but the details <b>differ</b>.</div>
                      <div className="mt-1"><span className="font-semibold">Duplicate's:</span> {Object.entries(r.details).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(", ") || "no extra details"}</div>
                      <div><span className="font-semibold">{tgtName}'s:</span> {Object.entries(r.target_version.details).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(", ") || "no extra details"}</div>
                      <label className="flex gap-2 items-start mt-1.5 cursor-pointer"><input type="checkbox" className="mt-0.5" checked={(rowRep[l.key] || []).includes(r.idx)} onChange={() => toggleRep(l.key, r.idx)} data-testid={`merge-replace-${l.key}-${r.idx}`} />
                        <span>Use the duplicate's details instead (e.g. its relationship, phone and guardian / decision-maker flags). Otherwise {tgtName}'s version is kept.</span></label>
                    </div>))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">Already on {tgtName} (kept as is)</div>
                {l.target_rows.length === 0 ? <div className="text-slate-400">None</div>
                  : <ul className="space-y-1.5">{l.target_rows.map((r) => <li key={r.idx}>{rowLine(r)}</li>)}</ul>}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (step === 5 && prev) {
    const sideCard = (title, tone, d) => (
      <div className={`rounded-md border p-3 text-xs space-y-1 ${tone}`}>
        <div className="text-[10px] uppercase tracking-wide font-semibold">{title}</div>
        <div><b>Preferred workers:</b> {names(d.preferred_staff)}</div>
        <div><b>Incompatible workers:</b> {names(d.incompatible_staff)}</div>
      </div>
    );
    const radio = (k, v, text) => (
      <label className="flex gap-2 items-start text-xs cursor-pointer">
        <input type="radio" className="mt-0.5" name={`rost-${k}`} checked={rost[k] === v} onChange={() => setRost({ ...rost, [k]: v })} data-testid={`merge-rost-${k}-${v}`} />
        <span>{text}</span>
      </label>
    );
    const html = (h) => <div className="mt-0.5 rounded border bg-white p-2 text-xs max-h-24 overflow-y-auto" dangerouslySetInnerHTML={{ __html: h ? sanitizeHtml(h) : "<em>Nothing recorded</em>" }} />;
    body = needs.length === 0 ? (
      <div className="text-sm text-slate-600" data-testid="merge-no-rost">The two profiles' rostering preferences don't conflict, so there is nothing to decide here. {tgtName}'s are kept as they are.</div>
    ) : (
      <div className="space-y-4">
        <p className="text-xs text-slate-600">The two profiles have different rostering preferences. For each one below, choose which version should be on <b>{tgtName}</b> after the merge.</p>
        {needs.includes("staff") && (
          <fieldset className="rounded-md border p-3 space-y-2" data-testid="merge-rost-staff">
            <legend className="px-1 text-xs font-semibold">{ROSTER_LABELS.staff}</legend>
            <div className="grid sm:grid-cols-2 gap-2">
              {sideCard(`Duplicate (${srcName})`, "border-red-200 bg-red-50/50", ros.source)}
              {sideCard(`Keeping (${tgtName})`, "border-emerald-300 bg-emerald-50/50", ros.target)}
            </div>
            {radio("staff", "keep", `Keep ${tgtName}'s lists as they are`)}
            {radio("staff", "duplicate", "Replace with the duplicate's lists")}
            {radio("staff", "both", "Combine both (a worker on either incompatible list stays incompatible)")}
            <p className="text-[10px] text-slate-500">Workers who become incompatible are not automatically taken off existing shifts — check the roster afterwards.</p>
          </fieldset>)}
        {["considerations", "reason"].filter((k) => needs.includes(k)).map((k) => (
          <fieldset key={k} className="rounded-md border p-3 space-y-2" data-testid={`merge-rost-${k}`}>
            <legend className="px-1 text-xs font-semibold">{ROSTER_LABELS[k]}</legend>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div><div className="text-[10px] uppercase tracking-wide font-semibold text-red-700">Duplicate ({srcName})</div>{html(ros.source[k])}</div>
              <div><div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Keeping ({tgtName})</div>{html(ros.target[k])}</div>
            </div>
            {radio(k, "keep", `Keep ${tgtName}'s text`)}
            {radio(k, "duplicate", "Replace with the duplicate's text")}
            {radio(k, "both", "Keep both (duplicate's text added underneath)")}
          </fieldset>))}
      </div>
    );
  } else if (step === DOC_STEP && prev) {
    body = groups.length === 0 ? (
      <div className="text-sm text-slate-600" data-testid="merge-no-docs">No documents or agreements clash between these profiles, so there is nothing to decide here. Documents move across with the status they have now.</div>
    ) : (
      <div className="space-y-4">
        <p className="text-xs text-slate-600">Only one version of each document or agreement should be current. For each one below, pick the version that should be the <b>current</b> one. Any other current version will be archived; archived versions you pick will be restored.</p>
        {groups.map((g) => (
          <fieldset key={g.key} className="rounded-md border p-3 space-y-1.5" data-testid={`merge-group-${g.key}`}>
            <legend className="px-1 text-xs font-semibold text-slate-800">{g.label}</legend>
            {g.docs.map((d) => (
              <label key={d.id} className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="radio" className="mt-0.5" name={g.key} checked={choices[g.key] === d.id} onChange={() => setChoices({ ...choices, [g.key]: d.id })} />
                <span><b>{d.title}</b> — {d.state}, from {d.owner === "duplicate" ? `${srcName} (duplicate)` : `${tgtName} (kept)`}, last changed {fmtDate(d.updated_at || d.created_at)}</span>
              </label>
            ))}
            <label className="flex items-start gap-2 text-xs cursor-pointer text-slate-500">
              <input type="radio" className="mt-0.5" name={g.key} checked={choices[g.key] === KEEP_AS_IS} onChange={() => setChoices({ ...choices, [g.key]: KEEP_AS_IS })} />
              <span>Leave all of these as they are (more than one may then be current)</span>
            </label>
          </fieldset>
        ))}
      </div>
    );
  } else if (step === FINAL_STEP && prev) {
    const entries = Object.entries(prev.counts);
    body = (
      <div className="space-y-3 text-xs">
        <div className="rounded-md border-2 border-red-500 bg-red-50 p-3 text-red-800 text-sm flex gap-2" data-testid="merge-final-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div><b>Final check — this is permanent.</b> After you press the red button, nothing can be moved back and there is no undo. Read the summary below carefully.</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="rounded-md border border-red-200 bg-red-50/50 p-3">
            <div className="font-semibold text-slate-800 mb-1">{srcName} <span className="font-normal text-slate-500">({src})</span> — AFTER</div>
            <ul className="list-disc ml-4 space-y-0.5 text-slate-700">
              <li>Set to <b>Inactive</b> and labelled "merged into {tgt}"</li>
              <li>Keeps <b>no</b> shifts, documents, notes or agreements</li>
              <li>Kept in the system for the audit trail, not deleted</li>
            </ul>
          </div>
          <div className="rounded-md border border-emerald-300 bg-emerald-50/50 p-3">
            <div className="font-semibold text-slate-800 mb-1">{tgtName} <span className="font-normal text-slate-500">({tgt})</span> — AFTER</div>
            <ul className="list-disc ml-4 space-y-0.5 text-slate-700">
              <li>Stays <b>{prev.target.status}</b> with its existing details</li>
              <li>Now holds all of the records below, plus its own</li>
            </ul>
          </div>
        </div>
        <div className="rounded-md border p-3">
          <div className="font-medium text-slate-700 mb-1">Records moving to {tgtName}</div>
          {entries.length === 0 ? <div>None</div> : <ul className="space-y-0.5">{entries.map(([k, n]) => <li key={k}><b>{n}</b> × {prev.count_labels[k]}</li>)}</ul>}
        </div>
        <div className="rounded-md border p-3" data-testid="merge-final-fields">
          <div className="font-medium text-slate-700 mb-1">Profile details</div>
          {fields.length === 0 ? <div>No differences — {tgtName}'s details stay as they are.</div> : (
            <ul className="space-y-0.5">{fields.map((f) => <li key={f.key}><b>{f.label}:</b> {{ keep: `keep ${tgtName}'s`, duplicate: "use the duplicate's", both: "keep both" }[fieldSel[f.key]]}</li>)}</ul>)}
        </div>
        <div className="rounded-md border p-3" data-testid="merge-final-roster">
          <div className="font-medium text-slate-700 mb-1">Roster</div>
          <div><b>{roster.total}</b> shift{roster.total === 1 ? "" : "s"} move from {srcName} to {tgtName}; none are left on {srcName}.</div>
          {(roster.invoiced > 0 || roster.approved_not_invoiced > 0) && <div className="mt-1 text-amber-800" data-testid="merge-final-invoiced">Includes {roster.invoiced} invoiced shift{roster.invoiced === 1 ? "" : "s"} and {roster.approved_not_invoiced} with an approved timesheet (not yet invoiced), which move with their invoice records.</div>}
          {pairs.length > 0 && <ul className="space-y-0.5 mt-1">{pairs.map((pr) => <li key={pr.key}>{pr.duplicate_shift.date} {pr.duplicate_shift.start} ({pr.duplicate_shift.worker}): {shiftSel[pr.key] === "cancel_duplicate_copy" ? "duplicate's copy CANCELLED (no charge)" : "both kept"}</li>)}</ul>}
        </div>
        <div className="rounded-md border p-3" data-testid="merge-final-profile">
          <div className="font-medium text-slate-700 mb-1">Contacts, stakeholders and rostering preferences</div>
          <ul className="space-y-1">
            {lists.map((l) => {
              const picked = l.source_rows.filter((r) => !r.already_on_target && (rowSel[l.key] || []).includes(r.idx));
              const left = l.source_rows.filter((r) => !r.already_on_target && !(rowSel[l.key] || []).includes(r.idx));
              const rep = l.source_rows.filter((r) => (rowRep[l.key] || []).includes(r.idx));
              return (<li key={l.key}><b>{l.label}:</b> {picked.length ? `copied — ${picked.map((r) => r.label).join(", ")}` : "none copied"}
                {rep.length > 0 && <span> · details replaced with the duplicate's: {rep.map((r) => r.label).join(", ")}</span>}
                {left.length > 0 && <span className="text-red-700"> · NOT copied and lost: {left.map((r) => r.label).join(", ")}</span>}</li>);
            })}
            {needs.length === 0 && <li>Rostering preferences: {tgtName}'s kept as they are.</li>}
            {needs.map((k) => <li key={k}><b>{ROSTER_LABELS[k]}:</b> {{ keep: `keep ${tgtName}'s`, duplicate: "replace with the duplicate's", both: k === "staff" ? "combine both lists" : "keep both texts" }[rost[k]]}</li>)}
          </ul>
        </div>
        <div className="rounded-md border p-3" data-testid="merge-final-docs">
          <div className="font-medium text-slate-700 mb-1">Your document decisions</div>
          {groups.length === 0 ? <div>No clashes — documents keep their current status.</div>
            : <ul className="space-y-1">{groups.map((g) => <li key={g.key}><b>{g.label}:</b> {describeChoice(g)}</li>)}</ul>}
        </div>
        <label className="flex gap-2 items-start text-sm text-red-800"><input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="merge-ack" />
          I understand this is permanent and cannot be undone.</label>
        <div>
          <Label className="text-xs">Type <b>MERGE</b> to unlock the button</Label>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1 h-9 w-40" data-testid="merge-type" autoComplete="off" />
        </div>
      </div>
    );
  }

  const canNext = [ready.checked && ready.correct, src && tgt && src !== tgt, true, fieldsDone, true, rostDone, shiftsDone, allChosen][step];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-participants-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand text-base">Merge duplicate profiles</DialogTitle>
          {!result && <div className="text-[11px] text-slate-500" data-testid="merge-step">Step {step + 1} of {STEPS.length}: {STEPS[step]}</div>}
        </DialogHeader>
        {body}
        <DialogFooter>
          {result ? <Button onClick={() => close(false)} style={{ backgroundColor: CRIMSON }}>Close</Button> : (<>
            <Button variant="outline" disabled={busy} onClick={() => (step === 0 ? close(false) : setStep(step === 2 ? 1 : step - 1))}>{step === 0 ? "Cancel" : "Back"}</Button>
            {step === 1 && <Button onClick={loadPreview} disabled={!canNext || busy} style={{ backgroundColor: CRIMSON }} data-testid="merge-next">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Next<ArrowRight className="h-4 w-4 ml-1" /></Button>}
            {[0, 2, 3, 4, 5, 6, DOC_STEP].includes(step) && <Button onClick={() => setStep(step + 1)} disabled={!canNext} style={{ backgroundColor: CRIMSON }} data-testid="merge-next">Next<ArrowRight className="h-4 w-4 ml-1" /></Button>}
            {step === FINAL_STEP && <Button onClick={submit} disabled={!canSubmit} className="bg-red-700 hover:bg-red-800 text-white" data-testid="merge-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Merge permanently — cannot be undone</Button>}
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
